package com.downdogapp.client.controllers.start

import com.downdogapp.client.*
import com.downdogapp.client.OfflinePractices.DownloadInProgress
import com.downdogapp.client.OfflinePractices.OfflinePractice
import com.downdogapp.client.controllers.playback.PlaybackViewController
import com.downdogapp.client.singleton.AlertAction
import com.downdogapp.client.singleton.App
import com.downdogapp.client.views.start.OfflinePracticesPageView
import kotlin.math.roundToInt

object OfflinePracticesPage : Page {

  override val view = OfflinePracticesPageView()

  override fun refreshPage() {
    view.refreshView()
  }

  val offlinePractices get() = OfflinePractices.offlinePractices.reversed()

  val downloadsInProgress get() = OfflinePractices.downloadsInProgress.reversed()

  fun getProgressText(downloadInProgress: DownloadInProgress) =
    // We scale progress so stitch progress is 0-50%, and download progress is 50-100%
    if (downloadInProgress.stitchProgress < 1) {
      Strings.DOWNLOADING_VIDEO_PROGRESS((downloadInProgress.stitchProgress * 50).roundToInt())
    } else {
      Strings.DOWNLOADING_VIDEO_PROGRESS((downloadInProgress.downloadProgress * 50).roundToInt() + 50)
    }

  fun promptCreateOfflinePractice() {
    NewPracticePage.validateCanStartPractice {
      OfflinePractices.validateCanDownloadPractice {
        App.alert(
          message = Strings.CREATE_OFFLINE_PRACTICE_EXPLANATION,
          positiveButton = AlertAction(Strings.YES_STRING) {
            createOfflinePractice()
          },
          negativeButton = AlertAction(Strings.CANCEL) {
          }
        )
      }
    }
  }

  private fun createOfflinePractice(isRetry: Boolean = false) {
    StartViewController.showSpinner()
    val desiredLength = SequenceSettings.selectedLengthOptionId
    SequenceSettings.useSequenceResponse {
      if (it == null) {
        logEvent("null_sequence")
        if (isRetry) {
          logEvent("sequence_retry_failed")
          App.alertNoInternet {
            App.fetchManifest {
              StartViewController.hideSpinner()
            }
          }
        } else {
          logEvent("sequence_retry")
          SequenceSettings.fetchSequences()
          createOfflinePractice(true)
        }
      } else if (it.errorMessage != null) {
        logEvent("sequence_error", mapOf("message" to it.errorMessage))
        App.alert(message = it.errorMessage) {
          App.fetchManifest {
            StartViewController.hideSpinner()
          }
        }
      } else {
        val sequenceTokens = it.sequenceTokens
        val length = sequenceTokens.keys.find { it == desiredLength }
        val selectedSequence = sequenceTokens[length]
        if (selectedSequence == null) {
          logEvent("selected_length_invalid")
          StartViewController.hideSpinner()
          App.alert(message = Strings.SEQUENCE_LENGTH_INVALID)
        } else {
          OfflinePractices.createOfflinePractice(sequenceTokens[length]!!) {
            // get new sequences in case they generate another practice
            SequenceSettings.fetchSequences()
            StartViewController.hideSpinner()
            refreshPage()
          }
        }
      }
    }
  }

  fun playOfflinePractice(practice: OfflinePractice) {
    NewPracticePage.validateCanStartPractice(checkOffline = false) {
      MediaSampleManager.onStartingPractice()

      createBackgroundMediaPlayer(
        video = true,
        loop = false
      ) { player ->
        player.setFile(CommonUri(practice.filePath))
        App.pushViewController(
          PlaybackViewController(
            sequence = practice.sequence,
            playlist = practice.playlist,
            player = player,
            playbackUrl = null
          )
        )
      }
    }
  }

  fun cancelOfflineDownload(downloadInProgress: DownloadInProgress) {
    App.alert(
      title = Strings.CANCEL_DOWNLOAD,
      message = Strings.CONFIRM_CANCEL_OFFLINE_DOWNLOAD,
      positiveButton = AlertAction(Strings.YES_STRING) {
        OfflinePractices.cancelDownload(downloadInProgress)
        view.refreshView()
      },
      negativeButton = AlertAction(Strings.NO_STRING) {
      }
    )
  }

  fun deleteOfflinePractice(practice: OfflinePractice) {
    App.alert(
      title = Strings.DELETE_OFFLINE_PRACTICE,
      message = Strings.CONFIRM_DELETE_OFFLINE_PRACTICE,
      positiveButton = AlertAction(Strings.YES_STRING) {
        OfflinePractices.deleteOfflinePractice(practice)
        view.refreshView()
      },
      negativeButton = AlertAction(Strings.NO_STRING) {
      }
    )
  }

  override val title: String
    get() = MANIFEST.offlinePracticesPageTitle

  override val shouldDisplay: Boolean
    get() = MANIFEST.displayOfflinePracticesPage
}
